import { Divider } from '@sqs/rosetta-elements';
import { Button, Flex } from '@sqs/rosetta-primitives';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { fetchSchedulingInstanceCalendars, updateUserInSchedulingInstance } from '../../apis/schedulingInstances';
import { SEARCH_SELECT_LIMIT } from '../../const/searchAndSelect';
import { usePlatformBreakpoint } from '../../hooks/usePlatformBreakpoint';
import { useI18n } from '../../i18n';
import { PageInfoQueryWithSearch } from '../../models/PaginatedResponse';
import { SchedulingInstancePermission, SchedulingInstancePermissionData } from '../../models/SchedulingInstance';
import { selectEnterprise } from '../../stores/currentUser';
import { sendErrorMessage, sendSuccessMessage } from '../../stores/messages';
import { RootState } from '../../stores/rootReducer';
import { fetchSchedulingInstances, selectAll } from '../../stores/schedulingInstances';
import { useAppDispatch, useAppSelector } from '../../stores/store';

import { GenericModal } from '../common/GenericModal';
import { ModalTitle } from '../common/ModalTitle';
import { AddUserToInstanceForm } from '../common/types/UserInstanceForm';
import { SetUserPermissions } from '../common/UserPermissions/SetUserPermissions';

interface UserChangeUserPermissionModalProps {
  readonly orgUserId: number;
  readonly permissionData: SchedulingInstancePermissionData;
  readonly closeModal: () => void;
  readonly refresh?: () => void;
}

export const UserChangeUserPermissionModal = ({
  orgUserId,
  permissionData,
  closeModal,
  refresh
}: UserChangeUserPermissionModalProps) => {
  const { T, t } = useI18n();
  const dispatch = useAppDispatch();
  const { isMobile } = usePlatformBreakpoint();

  const { enterpriseId, schedulingInstances } = useAppSelector((state: RootState) => ({
    enterpriseId: selectEnterprise(state.currentUser)?.id,
    schedulingInstances: selectAll(state.schedulingInstances)
  }));
  const schedulingInstanceId = permissionData.schedulingInstanceId!;

  const fetchCalendars = async (eid: number, options?: PageInfoQueryWithSearch) => {
    const res = await fetchSchedulingInstanceCalendars(eid, schedulingInstanceId, options);
    const calendars = res.data.map((calendar) => ({
      itemId: calendar.id,
      name: calendar.name,
      checked: permissionData.calendars.includes(calendar.id)
    }));
    return {
      data: calendars,
      pageInfo: res.pageInfo
    };
  };

  const formMethods = useForm<AddUserToInstanceForm>({
    defaultValues: async () => {
      if (enterpriseId) {
        const calendars = await fetchCalendars(enterpriseId, { limit: SEARCH_SELECT_LIMIT });
        return {
          schedulingInstanceId,
          permission: permissionData.permissions,
          allowExports: permissionData.allowExports,
          allItems: permissionData.allCalendars,
          items: calendars.data,
          totalItems: calendars.pageInfo.total,
          checkedItemIds: []
        };
      }
      return {
        schedulingInstanceId: 0,
        permission: '',
        allowExports: false,
        allItems: false,
        items: [],
        totalItems: 0,
        checkedItemIds: []
      };
    }
  });

  const {
    formState: { isDirty },
    handleSubmit,
    reset
  } = formMethods;

  const triggerCloseModal = () => {
    closeModal();
    reset();
  };

  const submitUserToInstance = handleSubmit((formData) => {
    const instance = schedulingInstances.find((i) => i.id === schedulingInstanceId);

    if (instance) {
      const calendars = !formData.allItems ? formData.checkedItemIds : [];

      const data: SchedulingInstancePermissionData = {
        permissions: formData.permission as SchedulingInstancePermission,
        allowExports: Boolean(formData.allowExports),
        calendars: calendars || [],
        allCalendars: Boolean(formData.allItems)
      };
      if (enterpriseId) {
        updateUserInSchedulingInstance(enterpriseId, schedulingInstanceId, orgUserId, data).
        then(() => {
          dispatch(
            sendSuccessMessage(t("User permission has been updated", null, { project: 'enterprise-dashboard' }))
          );
          dispatch(fetchSchedulingInstances(enterpriseId));
          triggerCloseModal();
          if (refresh) {
            refresh();
          }
        }).
        catch(() => {
          dispatch(
            sendErrorMessage(t("There was a problem updating the user's permission",
            null, {
              project: 'enterprise-dashboard' })

            )
          );
        });
      }
    } else {
      dispatch(
        sendErrorMessage(t("Unable to find scheduling instance data", null, { project: 'enterprise-dashboard' }))
      );
    }
  });

  return (
    <GenericModal
      closeModal={triggerCloseModal}
      modalActions={
      <Flex justifyContent="space-between" mt={isMobile ? 0 : 6}>
          <Button.Tertiary type="button" onClick={triggerCloseModal} size="medium">
            <T project="enterprise-dashboard">{"Cancel"}</T>
          </Button.Tertiary>
          <Button.Primary type="submit" size="medium" onClick={submitUserToInstance} disabled={!isDirty}>
            <T project="enterprise-dashboard">{"Save"}</T>
          </Button.Primary>
        </Flex>
      }>

      <FormProvider {...formMethods}>
        <ModalTitle>
          <T project="enterprise-dashboard">{"Change user permission"}</T>
        </ModalTitle>
        <Divider />
        <SetUserPermissions customFetchCalendars={fetchCalendars} preloadedItemStore={permissionData.calendars} />
      </FormProvider>
    </GenericModal>);

};